import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { precheck, uploadOne, MAX_FILES } from "@/lib/intake";
import { relPath } from "@/lib/dropFiles";
import { invalidate } from "@/hooks/use-resource";

let seq = 0;
const UPLOADING = ["venter", "laster", "tolkes"];
const lostInTransit = (e) => !e?.response || e.response.status >= 502; // the proxy gave up or the line dropped – the server keeps going
const OCR_WAIT = 45000; // ms the first reading waits for scanned files before it goes ahead without them
const fromServer = (f) => ({ key: `s-${f.id}`, serverId: f.id, name: f.name, size: f.size, status: f.status, pct: 100, error: f.error || null, docs: f.docs, note: f.note, attempt: f.attempt, at: f.uploaded_at });

/**
 * The intake conversation: one session that is created on the first file or message and saved on every step.
 * Uploads, the analysis and the dialogue run through one serial queue, so the assistant never answers before the
 * documents it was asked about are read. `onSession` fires once the session exists (the page puts it in the URL).
 */
export function useIntake({ sid: initialSid = null, onSession } = {}) {
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [rows, setRows] = useState([]);
  const [phase, setPhase] = useState(initialSid ? "loading" : "idle"); // idle | loading | uploading | analyzing | chatting | creating
  const [loadError, setLoadError] = useState(null);
  const sidRef = useRef(null);
  const createdHere = useRef(null);
  const queue = useRef(Promise.resolve());
  const rowsRef = useRef([]);
  const sessRef = useRef(null);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { sessRef.current = session; }, [session]);

  const take = useCallback((data) => {
    if (!data) return;
    const { documents: docs, ...s } = data;
    sessRef.current = s; // synchronous – the serial queue reads it before React has re-rendered
    setSession(s);
    if (docs) setDocuments(docs);
  }, []);
  const patchRow = useCallback((key, patch) => {
    rowsRef.current = rowsRef.current.map((r) => (r.key === key ? { ...r, ...patch } : r));
    setRows(rowsRef.current);
  }, []);
  /** The server's verdict per file (after OCR) – rows still uploading are the client's own. */
  const syncRows = useCallback((files) => {
    if (!files) return;
    const byId = Object.fromEntries(files.map((f) => [f.id, f]));
    rowsRef.current = rowsRef.current.map((row) => { const f = row.serverId && !UPLOADING.includes(row.status) && byId[row.serverId]; return f && f.status !== row.status ? { ...row, status: f.status, note: f.note, docs: f.docs, error: f.error || null } : row; });
    setRows(rowsRef.current);
  }, []);

  useEffect(() => {
    if (!initialSid || createdHere.current === initialSid) return;
    sidRef.current = initialSid;
    let alive = true;
    api.get(`/inntak/${initialSid}`).then((r) => {
      if (!alive) return;
      take(r.data);
      rowsRef.current = (r.data.files || []).map(fromServer);
      setRows(rowsRef.current);
      setPhase("idle");
      // handed over from Innboks, the example folder (or interrupted): the documents are in, nothing has been read yet – read
      // now, after any scanned file (same single reading as an upload)
      const usable = (r.data.files || []).some((f) => !["feil", "duplikat", ...UPLOADING].includes(f.status));
      if (r.data.status === "open" && !r.data.proposal && usable) settle();
    }).catch((e) => { if (alive) { setLoadError(errMsg(e)); setPhase("idle"); } });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSid, take]);

  const ensureSession = useCallback(async () => {
    if (sidRef.current) return sidRef.current;
    const r = await api.post("/inntak", { text: "" });
    sidRef.current = r.data.id;
    createdHere.current = r.data.id;
    take(r.data);
    onSession?.(r.data.id);
    invalidate("/inntak");
    return r.data.id;
  }, [take, onSession]);

  const enqueue = useCallback((fn) => { const p = queue.current.then(fn, fn); queue.current = p.catch(() => {}); return p; }, []);

  /** A slow reading can outlive the proxy's patience (~60 s) – the server finishes anyway, so wait for its answer instead of failing. */
  const outlast = useCallback(async (sid, done) => {
    for (let i = 0; i < 60; i += 1) {
      await new Promise((res) => { setTimeout(res, 3000); });
      try { const r = await api.get(`/inntak/${sid}`); if (done(r.data)) return r.data; } catch { /* next tick */ }
    }
    return null;
  }, []);

  const analyze = useCallback(async (text) => {
    const sid = await ensureSession();
    setPhase("analyzing");
    const before = sessRef.current?.proposal?.analyzed_at || null;
    const apply = (d) => { take(d); syncRows(d.files); invalidate("/inntak"); }; // OCR that finished before this reading is in it
    try {
      apply((await api.post(`/inntak/${sid}/analyze`, { text: text ?? null })).data);
    } catch (e) {
      const got = lostInTransit(e) ? await outlast(sid, (d) => d.proposal?.analyzed_at && d.proposal.analyzed_at !== before) : null;
      if (got) apply(got); else toast.error(errMsg(e));
    } finally {
      setPhase("idle");
    }
  }, [ensureSession, take, syncRows, outlast]);

  const upload = useCallback((row) => enqueue(async () => {
    const sid = await ensureSession();
    setPhase("uploading");
    patchRow(row.key, { status: "laster", pct: 0, error: null });
    try {
      const r = await uploadOne(`/inntak/${sid}/files`, row.file, { path: relPath(row.file), replace_file_id: row.serverId || null }, (pct) => patchRow(row.key, { pct, status: pct === 100 ? "tolkes" : "laster" }));
      const f = r.data.file;
      patchRow(row.key, { status: f.status, pct: 100, error: f.error || null, serverId: f.id, docs: f.docs, note: f.note, attempt: f.attempt, at: f.uploaded_at });
      take(r.data.session);
    } catch (e) {
      patchRow(row.key, { status: "feil", pct: null, error: errMsg(e), serverId: row.serverId || null });
    }
    setPhase("idle");
  }), [enqueue, ensureSession, patchRow, take]);

  /** Once the uploads have settled, the assistant reads what came in – unasked, and only once per burst: a folder dropped in
   * several batches gives one analysis that updates the same findings card, never a stack of them. Scanned files are
   * waited for (up to OCR_WAIT) so their text is in that one reading; the OCR poll settles again when they are done. */
  const settleTimer = useRef(null);
  const ocrSince = useRef(null);
  const settle = useCallback((force = false) => {
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => enqueue(async () => {
      const s = sessRef.current;
      const now = rowsRef.current;
      if (now.some((r) => UPLOADING.includes(r.status))) return; // the batch behind this one settles again
      const reading = now.some((r) => r.status === "leses");
      if (reading && !ocrSince.current) ocrSince.current = Date.now();
      if (reading && !s?.proposal && Date.now() - ocrSince.current < OCR_WAIT) return;
      const usable = now.some((r) => !["feil", "duplikat", ...UPLOADING].includes(r.status));
      if (s && (force || !s.proposal) && usable) await analyze(null);
    }), 1500);
  }, [enqueue, analyze]);
  useEffect(() => () => clearTimeout(settleTimer.current), []);

  const addFiles = useCallback((list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;
    const cur = rowsRef.current;
    if (cur.length + incoming.length > MAX_FILES) toast.error(`Maks ${MAX_FILES} filer per inntak`);
    const fresh = incoming.slice(0, Math.max(0, MAX_FILES - cur.length)).map((file) => {
      const reason = precheck(file);
      return { key: `f${++seq}`, file, name: file.name, size: file.size, status: reason ? "feil" : "venter", pct: null, error: reason, local: Boolean(reason), at: new Date().toISOString() };
    });
    rowsRef.current = [...cur, ...fresh];
    setRows(rowsRef.current);
    fresh.filter((r) => !r.local).forEach((r) => upload(r));
    settle();
  }, [upload, settle]);

  const retry = useCallback((key) => {
    const row = rowsRef.current.find((r) => r.key === key);
    if (!row) return;
    const reason = precheck(row.file);
    if (reason) { toast.error(reason); return; }
    upload({ ...row, local: false });
    settle();
  }, [upload, settle]);

  const remove = useCallback(async (key) => {
    const row = rowsRef.current.find((r) => r.key === key);
    setRows((cur) => cur.filter((r) => r.key !== key));
    if (row?.serverId && sidRef.current) {
      try { const r = await api.delete(`/inntak/${sidRef.current}/files/${row.serverId}`); take(r.data); } catch (e) { toast.error(errMsg(e)); }
    }
  }, [take]);

  /** Scanned files are read with OCR on the server after upload: follow them, and read the findings again once the text is in. */
  const ocrReading = rows.some((r) => r.status === "leses") && !rows.some((r) => UPLOADING.includes(r.status));
  useEffect(() => {
    if (!ocrReading || !sidRef.current) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await api.get(`/inntak/${sidRef.current}`);
        const byId = Object.fromEntries((r.data.files || []).map((f) => [f.id, f]));
        // Read again only when OCR brought new text: a scanned document that is now read, or a photo with words on it.
        const gained = rowsRef.current.some((row) => { const f = row.serverId && byId[row.serverId]; return row.status === "leses" && f && f.status !== "leses" && (f.status !== "vedlegg" || /ord lest med OCR/.test(f.note || "")); });
        syncRows(r.data.files);
        take(r.data);
        const done = !(r.data.files || []).some((f) => f.status === "leses");
        const first = !sessRef.current?.proposal;
        if (done) { ocrSince.current = null; if (first || gained) settle(!first); }
        else if (first && ocrSince.current && Date.now() - ocrSince.current >= OCR_WAIT) settle(); // a slow scan never holds the reading back for long
      } catch { /* next tick */ }
    }, 3000);
    return () => clearInterval(t);
  }, [ocrReading, take, settle, syncRows]);

  /** A message. Before anything is analysed it is the description; after that it is dialogue about the documents. */
  const send = useCallback((text) => enqueue(async () => {
    const sid = await ensureSession();
    setPhase("chatting");
    const before = (sessRef.current?.messages || []).length;
    try {
      const r = await api.post(`/inntak/${sid}/chat`, { message: text });
      take(r.data);
    } catch (e) {
      const got = lostInTransit(e) ? await outlast(sid, (d) => (d.messages || []).length >= before + 2 && d.messages[d.messages.length - 1].role === "assistant") : null;
      if (got) { take(got); syncRows(got.files); } else toast.error(errMsg(e));
    } finally {
      setPhase("idle");
    }
  }), [enqueue, ensureSession, take, syncRows, outlast]);

  const setOverrides = useCallback(async (overrides) => {
    const sid = sidRef.current;
    if (!sid) return;
    try { take((await api.patch(`/inntak/${sid}`, { overrides })).data); } catch (e) { toast.error(errMsg(e)); }
  }, [take]);

  const createWork = useCallback(async (into) => {
    const sid = sidRef.current;
    if (!sid) return null;
    setPhase("creating");
    try {
      await queue.current;
      const r = await api.post(`/inntak/${sid}/work`, into ? { into } : {});
      window.dispatchEvent(new Event("projects:changed"));
      return r.data;
    } catch (e) {
      toast.error(errMsg(e));
      setPhase("idle");
      return null;
    }
  }, []);

  /** Forget the current conversation locally – for «Nytt arbeid» while one is open. Nothing is deleted. */
  const reset = useCallback(() => {
    sidRef.current = null;
    createdHere.current = null;
    rowsRef.current = [];
    setRows([]); setSession(null); setDocuments([]); setPhase("idle"); setLoadError(null);
  }, []);

  const discard = useCallback(async () => {
    const sid = sidRef.current;
    sidRef.current = null;
    setRows([]); setSession(null); setDocuments([]); setPhase("idle");
    if (sid) api.delete(`/inntak/${sid}`).then(() => invalidate("/inntak")).catch(() => {});
  }, []);

  const uploading = rows.some((r) => UPLOADING.includes(r.status));
  const busy = phase !== "idle" || uploading;
  return { session, documents, rows, phase, busy, uploading, loadError, addFiles, retry, remove, send, setOverrides, createWork, discard, reset, sid: sidRef.current };
}
